<template>
  <div class="chatbot-container">
    <div v-if="isOpen" class="chatbot-window">
      <div class="chatbot-header">
        <div>
          <div class="chatbot-header__title"><span>🤖</span> Fun識香港 AI 助手</div>
          <div class="chatbot-header__quota">請合理使用 AI 助手</div>
        </div>
        <button class="chatbot-header__close" @click="toggleChat">&times;</button>
      </div>

      <div ref="messagesContainer" class="chatbot-messages" @click="handleMessageClick">
        <div
          v-for="(msg, idx) in messages"
          :key="idx"
          class="chatbot-msg"
          :class="{
            'chatbot-msg--bot': msg.role === 'bot',
            'chatbot-msg--user': msg.role === 'user',
            'chatbot-msg--error': msg.role === 'error'
          }"
          v-html="formatMessage(msg.content)"
        />
        <div v-if="isLoading" class="chatbot-typing"><span></span><span></span><span></span></div>
      </div>

      <div class="chatbot-input">
        <input
          ref="inputField"
          v-model="userInput"
          class="chatbot-input__field"
          placeholder="例如：我想去中環玩…"
          :disabled="isLoading"
          @keydown.enter="handleSend"
        />
        <button
          class="chatbot-input__send"
          :disabled="isLoading || !userInput.trim()"
          @click="handleSend"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { sendMessage } from './chatService'

export default {
  name: 'AiChatbot',
  data () {
    return {
      isOpen: false,
      userInput: '',
      messages: [],
      conversationHistory: [],
      isLoading: false
    }
  },
  computed: {
    ...mapGetters({
      me: 'auth/me'
    })
  },
  mounted () {
    this.restoreConversation()
    this.emitter.on('toggleChatbot', this.toggleChat)
  },
  beforeUnmount () {
    this.emitter.off('toggleChatbot', this.toggleChat)
  },
  methods: {
    toggleChat () {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        if (this.messages.length === 0) {
          this.messages.push({
            role: 'bot',
            content: '你好！我係 Fun識香港 AI 助手 🎒\n\n你可以問我關於香港景點嘅問題，例如：\n• 「我想去中環玩，有咩景點推薦？」\n• 「九龍城有咩歷史景點？」\n• 「幫我規劃尖沙咀半日遊路線」\n\n我會根據最新景點資料為你推薦同規劃路線！'
          })
          this.persistConversation()
        }
        this.$nextTick(() => {
          if (this.$refs.inputField) this.$refs.inputField.focus()
        })
      }
    },
    async handleSend () {
      const text = this.userInput.trim()
      if (!text || this.isLoading) return

      this.messages.push({ role: 'user', content: text })
      this.userInput = ''
      this.isLoading = true
      this.scrollToBottom()
      this.persistConversation()

      const result = await sendMessage(text, this.conversationHistory)
      this.isLoading = false

      if (result.success) {
        this.messages.push({ role: 'bot', content: result.message })
        this.conversationHistory.push(
          { role: 'user', content: text },
          { role: 'assistant', content: result.message }
        )
        if (this.conversationHistory.length > 10) {
          this.conversationHistory = this.conversationHistory.slice(-10)
        }
      } else {
        this.messages.push({ role: 'error', content: result.message })
      }

      this.persistConversation()
      this.scrollToBottom()
    },
    formatMessage (text) {
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      const withLinks = escaped.replace(
        /\/places\/(\d+)\b/g,
        '<a href="/places/$1" data-place-id="$1" class="chatbot-link">查看景點詳情</a>'
      ).replace(
        /(https?:\/\/[^\s<]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer" class="chatbot-link">$1</a>'
      )

      return withLinks.replace(/\n/g, '<br>')
    },
    conversationStorageKey () {
      return `chatbot-conversation:${this.me.id}`
    },
    restoreConversation () {
      try {
        const savedConversation = window.sessionStorage.getItem(this.conversationStorageKey())
        if (!savedConversation) return

        const { messages, history } = JSON.parse(savedConversation)
        if (Array.isArray(messages)) this.messages = messages
        if (Array.isArray(history)) this.conversationHistory = history
      } catch (error) {
        window.sessionStorage.removeItem(this.conversationStorageKey())
      }
    },
    persistConversation () {
      if (!this.me.id) return

      window.sessionStorage.setItem(this.conversationStorageKey(), JSON.stringify({
        messages: this.messages.slice(-20),
        history: this.conversationHistory
      }))
    },
    handleMessageClick (event) {
      const link = event.target.closest('a[data-place-id]')
      if (!link) return

      event.preventDefault()
      this.$router.push(`/places/${link.dataset.placeId}`)
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const element = this.$refs.messagesContainer
        if (element) element.scrollTop = element.scrollHeight
      })
    }
  }
}
</script>

<style lang="scss">
@import "style";
</style>
